import { useCallback, useEffect, useMemo, useRef } from 'react';

import UAParser from 'ua-parser-js';

import { useAnalytics } from '@backstage/core-plugin-api';

import { confidenceApi } from '../../../apis/Apis';

const getDeviceInfo = () => {
  const parser = new UAParser();
  const result = parser.getResult();

  let deviceType = 'desktop';
  if (result.device.type === 'mobile') deviceType = 'mobile';
  else if (result.device.type === 'tablet') deviceType = 'tablet';

  return {
    user_agent: navigator.userAgent,
    browser_name: result.browser.name || 'Unknown',
    browser_version: result.browser.version || 'Unknown',
    os_name: result.os.name || 'Unknown',
    device_type: deviceType,
  };
};

const getUtmParams = (searchParams: URLSearchParams) => {
  const params: Record<string, string> = {};

  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmCampaign = searchParams.get('utm_campaign');
  const utmTerm = searchParams.get('utm_term');
  const utmContent = searchParams.get('utm_content');

  if (utmSource) params.utm_source = utmSource;
  if (utmMedium) params.utm_medium = utmMedium;
  if (utmCampaign) params.utm_campaign = utmCampaign;
  if (utmTerm) params.utm_term = utmTerm;
  if (utmContent) params.utm_content = utmContent;

  return params;
};

// Sanitize URL to prevent leaking sensitive data (tokens, OAuth codes, emails, etc.)
// Only captures origin + pathname, strips query params and fragments
const sanitizeUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    // Return only origin + pathname (no query params or fragments)
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    // If URL parsing fails, return null to avoid leaking malformed URLs
    return null;
  }
};

const getReferrerInfo = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const utm = getUtmParams(searchParams);
  const referrerUrl = sanitizeUrl(document.referrer);
  const landingPageUrl = sanitizeUrl(window.location.href);

  const info: {
    referrer_url?: string;
    landing_page_url?: string;
    utm?: Record<string, string>;
  } = {};

  if (referrerUrl) info.referrer_url = referrerUrl;
  if (landingPageUrl) info.landing_page_url = landingPageUrl;
  if (Object.keys(utm).length > 0) info.utm = utm;

  return info;
};

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

export type SignupFlowVariant =
  | 'workspace_wizard'
  | 'company_wizard'
  | 'user_info_wizard';

export type SignupStepEvent = {
  step_id: string;
  step_name: string;
  step_index: number;
  step_count: number;
  step_version?: string;
  navigation?: {
    entry_mode: 'next' | 'back' | 'direct';
    previous_step_id?: string;
  };
};

export type SignupEventTrigger = 'impression' | 'completion' | 'error';

/**
 * Hook to track signup flow events with enhanced analytics.
 *
 * Implements the tracking schema proposed in the signup flow tracking document:
 * - Generates unique run_id for each signup flow
 * - Captures referrer and UTM parameters
 * - Tracks device and browser information
 * - Provides structured event tracking for steps and completion
 */
export const useSignupTracking = (variant: SignupFlowVariant) => {
  const analytics = useAnalytics();
  const runIdRef = useRef<string>();
  const startTimeRef = useRef<number>();
  const previousStepRef = useRef<string>();
  const hasTrackedStartRef = useRef(false);

  // Generate or retrieve run_id from sessionStorage (persists across remounts)
  if (!runIdRef.current) {
    const storedRunId = sessionStorage.getItem(`signup-run-id-${variant}`);
    if (storedRunId) {
      runIdRef.current = storedRunId;
      const storedStartTime = sessionStorage.getItem(
        `signup-start-time-${variant}`,
      );
      startTimeRef.current = storedStartTime
        ? parseInt(storedStartTime, 10)
        : Date.now();
    } else {
      runIdRef.current = generateId();
      startTimeRef.current = Date.now();
      sessionStorage.setItem(`signup-run-id-${variant}`, runIdRef.current);
      sessionStorage.setItem(
        `signup-start-time-${variant}`,
        startTimeRef.current.toString(),
      );
    }
  }

  // Get visitor_id from Confidence SDK context
  const visitorId = useMemo(() => confidenceApi.getContext().visitor_id, []);

  // Memoize static context that doesn't change during the flow
  const staticContext = useMemo(
    () => ({
      run_id: runIdRef.current!,
      visitor_id: visitorId,
      flow: {
        flow_name: 'signup',
        variant,
        flow_version: 'v3',
      },
      referrer: getReferrerInfo(),
      device: getDeviceInfo(),
    }),
    [variant, visitorId],
  );

  /**
   * Track a signup step event
   */
  const trackStep = useCallback(
    (
      trigger: SignupEventTrigger,
      step: SignupStepEvent,
      additionalData?: {
        outcome?: {
          status: 'success' | 'error';
          latency_ms?: number;
          error_message?: string;
        };
      },
    ) => {
      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'step',
        trigger,
        event_time: new Date().toISOString(),
        ...staticContext,
        step,
        ...(additionalData || {}),
      };

      // Send to analytics using the existing Confidence analytics
      analytics.captureEvent('signup', 'signup-step', {
        attributes: eventData as any,
      });

      // Update previous step for navigation tracking
      previousStepRef.current = step.step_id;
    },
    [analytics, staticContext],
  );

  /**
   * Track flow completion
   */
  const trackCompletion = useCallback(
    (
      status: 'success' | 'abandoned' | 'error',
      additionalData?: {
        error_message?: string;
        final_step_id?: string;
      },
    ) => {
      const latencyMs = startTimeRef.current
        ? Date.now() - startTimeRef.current
        : undefined;

      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'completion',
        trigger: 'completion',
        event_time: new Date().toISOString(),
        ...staticContext,
        outcome: {
          status,
          latency_ms: latencyMs,
          ...(additionalData || {}),
        },
      };

      analytics.captureEvent('signup', 'signup-completion', {
        attributes: eventData as any,
      });

      // Clear sessionStorage on successful completion so next signup gets fresh run_id
      if (status === 'success') {
        sessionStorage.removeItem(`signup-run-id-${variant}`);
        sessionStorage.removeItem(`signup-start-time-${variant}`);
      }
    },
    [analytics, staticContext, variant],
  );

  /**
   * Track user intent (role and goals)
   */
  const trackUserIntent = useCallback(
    (role: string, goals: string[]) => {
      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'user_intent',
        trigger: 'completion',
        event_time: new Date().toISOString(),
        ...staticContext,
        user_intent: {
          role,
          goals,
        },
      };

      analytics.captureEvent('signup', 'user-intent', {
        attributes: eventData as any,
      });
    },
    [analytics, staticContext],
  );

  // Track flow start on mount (only once per run_id)
  useEffect(() => {
    if (hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;

    const eventData = {
      event_name: 'signup_flow_event',
      event_type: 'flow_start',
      trigger: 'impression',
      event_time: new Date().toISOString(),
      ...staticContext,
    };

    analytics.captureEvent('signup', 'signup-start', {
      attributes: eventData as any,
    });
  }, [analytics, staticContext]);

  return {
    runId: runIdRef.current!,
    visitorId,
    trackStep,
    trackCompletion,
    trackUserIntent,
    getPreviousStepId: () => previousStepRef.current,
  };
};
